import { Outlet, useLocation } from 'react-router-dom';
import { SidebarProvider, useSidebar } from './SidebarContext';
import { ExternalNavigation } from './ExternalNavigation';
import { PortalTopNav } from './PortalTopNav';
import { ToastHost } from '../ui/Toast';

/**
 * Phase 23 D-01 / D-02 / v4.0 Landmine #8:
 *   - This shell MUST NOT import useAuth from services/authContext. The
 *     internal Layout.tsx uses the internal auth context; the external
 *     surface MUST stay isolated.
 *   - The internal admin banners are intentionally NOT mounted here —
 *     external users never see them.
 */
function ExternalLayoutContent() {
  const { isCollapsed, mobileOpen, setMobileOpen } = useSidebar();
  const location = useLocation();

  // Phase 57 PORTAL-01: portal routes use a top-nav instead of the left sidebar.
  // Non-portal routes keep the existing layout exactly as-is.
  const isPortal = location.pathname.startsWith('/external/portal');

  if (isPortal) {
    return (
      <div className="flex flex-col min-h-screen">
        <PortalTopNav />
        <main className="flex h-[calc(100vh-56px)]">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white shadow-sm flex h-16 items-center justify-between px-4">
        <button
          type="button"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          aria-label="Open menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
        <h1 className="text-lg font-semibold text-primary-600">Monksheets</h1>
        <div className="w-10" />
      </header>

      <ExternalNavigation />

      <main
        className={`transition-all duration-300 pt-16 lg:pt-0 ${isCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}
      >
        <div className="px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export function ExternalLayout() {
  return (
    <ToastHost>
      <SidebarProvider>
        <ExternalLayoutContent />
      </SidebarProvider>
    </ToastHost>
  );
}
